import React from "react";
import { Link } from "react-router-dom";
import { Card } from "./ui/card";
import { BLOGS } from "../lib/blogs";
import { Clock, ArrowRight } from "lucide-react";

/**
 * Health Blogs section on the landing page — Max Healthcare style.
 * Shows the 3 most-recent posts with cover image, category chip, title,
 * read-time, and a "View all →" link to /blog.
 */
export default function HealthBlogsSection() {
  const posts = BLOGS.slice(0, 3);
  return (
    <section className="bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-10">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-teal-700">Health Blogs</div>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-slate-900">
              Learn before you decide.
            </h2>
            <p className="mt-2 text-slate-600 text-sm sm:text-base max-w-2xl">
              Plain-English explainers on common neuro conditions, written by NeuroOpinion India specialists.
            </p>
          </div>
          <Link
            to="/blog"
            className="text-sm font-semibold text-teal-700 hover:text-teal-800 inline-flex items-center gap-1.5"
            data-testid="blogs-view-all-link"
          >
            View all articles <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group"
            >
              <Card className="overflow-hidden border-slate-200 lift h-full flex flex-col">
                <div className="aspect-[16/10] w-full relative overflow-hidden bg-gradient-to-br from-teal-50 to-blue-50">
                  {p.cover ? (
                    <img
                      src={p.cover}
                      alt={p.title}
                      className="w-full h-full object-cover transition duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-teal-700 text-5xl font-heading font-bold opacity-30">
                      {p.title[0]}
                    </div>
                  )}
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-white/90 backdrop-blur text-teal-700 border border-teal-200 shadow-sm">
                      {p.category}
                    </span>
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-heading font-semibold text-lg text-slate-900 leading-snug line-clamp-2 group-hover:text-teal-700 transition">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3 flex-1">
                    {p.excerpt}
                  </p>
                  <div className="mt-4 flex items-center justify-between pt-3 border-t border-slate-100 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5"/> {p.read_minutes} min read
                    </span>
                    <span className="inline-flex items-center gap-1 text-teal-700 font-semibold">
                      Read <ArrowRight className="w-3.5 h-3.5"/>
                    </span>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
